import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";

import { ConflictReviewModal } from "@/components/entity/ConflictReviewModal";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { firebaseReady, getUserEntityId } from "@/providers/firebase/authProvider";
import { getEntity, updateEntity, type EntityData } from "@/services/api/entityApi";

/**
 * THE PERSON'S OWN PROFILE — HIS ENTITY, AND A WAY TO KEEP IT IN SYNC (P206 T0306).
 *
 * **Who does what:** the person tells Raisy where his own page is; Raisy reads it and keeps what it
 * learned on HIS entity; he looks at what Raisy holds about him whenever he wants.
 * **What he sees:** the facts themselves (name, role, what he does, where he is, his links), where
 * they came from, and when they were last brought in.
 * **What he feels:** *"never re-type what's known"* (FTUE W6) and *"my data, my machines"* (Sam W6).
 *
 * ── WHAT THIS PAGE REPLACED, AND WHY (the PO's instruction, IN-05) ───────────────────────────────
 * It was the inherited Atomic CRM template's profile page. Three things were wrong with it, and all
 * three were found by reading the code rather than by looking at it:
 *
 *   1. **Its cards described a product that does not exist here.** It printed an MCP server address
 *      at the template's Supabase edge function, and offered an inbound-email address — both belong
 *      to the template's own Supabase backend. T0306 removes them; T0307 proves the MCP card is gone
 *      from the SERVED bundle (a deleted import would otherwise surface as a ReferenceError).
 *   2. **Its form could not save.** It wrote through `dataProvider.salesUpdate` — and `salesUpdate`
 *      exists only in the FAKEREST provider (`src/providers/fakerest/dataProvider.ts`, an in-memory
 *      `ra-data-fakerest` database), while the raisy BE has no `/sales` route at all. So editing his
 *      name there mutated memory, showed *"Your profile has been updated"*, and changed nothing in
 *      the product. That is exactly the defect D-206-001 bans on this surface: **a control that
 *      cannot save must not exist.**
 *   3. **It was a second, separate idea of what a profile is.** Every other surface in Raisy reads
 *      the ENTITY. So the page now reads the person's own entity — the same source as the rest of
 *      the product — and writes to it.
 *
 * ── THE SCRAPE REUSES WHAT EXISTS; IT IS NOT A NEW SCRAPER (the task's own condition) ───────────
 * "Bring in what that page says" stores the address on his entity and then opens the EXISTING
 * `ConflictReviewModal` in its Mode A — the flow already used by the duplicate finder and the entity
 * wizard. That flow calls `POST /entity/merge-preview`, which re-runs the existing `raisy_78`
 * extraction against `entity.extraction_url`, **auto-fills fields that are empty, and stages
 * anything that disagrees with a non-empty value as a conflict for HIM to answer** (it defaults to
 * keeping what is already there, so a deliberate human edit is never silently overwritten). Nothing
 * new was written for the scrape itself.
 *
 * ── WHY IT DOES NOT SCRAPE ON LOAD ──────────────────────────────────────────────────────────────
 * A page that re-read his page every visit would spend money on every visit, and would let a source
 * that changed underneath him rewrite what he had corrected. The facts are the entity row, so a
 * second visit SHOWS them without any work — and a fresh look happens only when he asks for one.
 *
 * ── SIGN-IN IS NOT DUPLICATED HERE ──────────────────────────────────────────────────────────────
 * T0206 deliberately placed the person's account block — name, photograph, language, appearance,
 * password, log out — inside row 7 of `/settings`, *"where it belongs, instead of being a second
 * page"*. This page therefore does not grow a second copy of it, and points there. ⚠️ Recorded for a
 * later task rather than changed here: that block's password button goes through the same fakerest
 * `updatePassword`, while the REAL reset lives at `/forgot-password` (Firebase
 * `sendPasswordResetEmail`). Re-pointing that button is not this task's change to make.
 */
type Load =
  | { state: "loading" }
  | { state: "unavailable"; reason: string }
  | { state: "ready"; entity: EntityData };

const reasonOf = (e: unknown) =>
  e instanceof Error ? e.message : typeof e === "string" ? e : "it did not answer";

const looksLikeAddress = (value: string) => /^https?:\/\/\S+\.\S+/.test(value.trim());

export const ProfilePage = () => {
  const [ownEntityId, setOwnEntityId] = useState<string | null>(() => getUserEntityId());
  /**
   * The cookie that carries his own entity id is written AFTER Firebase settles. Reading it once at
   * mount leaves a cold visit (a refresh, a deep link) with `null` for the life of the page — the
   * same defect T0300 fixed on `/settings`, where six rows said "no company is chosen yet" while the
   * data was fine. So it is re-read once auth has resolved, and the page distinguishes "still
   * settling" from "there really is no entity" rather than calling both of them empty.
   */
  const [authSettled, setAuthSettled] = useState(false);
  useEffect(() => {
    let live = true;
    firebaseReady
      .then(() => { if (live) { setOwnEntityId(getUserEntityId()); setAuthSettled(true); } })
      .catch(() => { if (live) setAuthSettled(true); });
    return () => { live = false; };
  }, []);

  const [load, setLoad] = useState<Load>({ state: "loading" });
  const read = useCallback(async () => {
    if (!ownEntityId) {
      if (!authSettled) { setLoad({ state: "loading" }); return; }
      setLoad({ state: "unavailable", reason: "your sign-in is not connected to an entity yet" });
      return;
    }
    setLoad({ state: "loading" });
    try {
      const res = await getEntity(ownEntityId);
      if (!res.status || !res.data) throw new Error(res.message || "the read did not answer");
      setLoad({ state: "ready", entity: res.data });
    } catch (e) {
      setLoad({ state: "unavailable", reason: reasonOf(e) });
    }
  }, [ownEntityId, authSettled]);
  useEffect(() => { void read(); }, [read]);

  /** The address he has typed, and whether it still needs storing on the entity before the read. */
  const entity = load.state === "ready" ? load.entity : null;
  const storedAddress = entity?.extraction_url ?? entity?.entity_links?.linkedin ?? entity?.entity_links?.website ?? "";
  const [draft, setDraft] = useState<string | null>(null);
  const address = draft ?? storedAddress;

  const [busy, setBusy] = useState(false);
  const [saveFailed, setSaveFailed] = useState<string | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [broughtIn, setBroughtIn] = useState(false);

  const saveAddressThenRead = async () => {
    if (!entity) return;
    const wanted = address.trim();
    setSaveFailed(null);
    if (!wanted || !looksLikeAddress(wanted)) {
      setSaveFailed("That does not look like a web address — it should start with http:// or https://");
      return;
    }
    setBusy(true);
    try {
      if (wanted !== (entity.extraction_url ?? "")) {
        const res = await updateEntity(entity.entity_id, { extraction_url: wanted });
        if (!res.status) throw new Error(res.message || "the address was not stored");
      }
      setReviewOpen(true);
    } catch (e) {
      setSaveFailed(reasonOf(e));
    } finally {
      setBusy(false);
    }
  };

  const onResolved = async () => {
    setReviewOpen(false);
    setBroughtIn(true);
    await read();
  };

  const facts = entity ? (
    <dl className="space-y-2 text-sm">
      <Fact label="Name" value={entity.entity_name || "not set yet"} />
      <Fact label="This is" value={[entity.entity_type, entity.entity_type_sub, entity.person_role].filter(Boolean).join(" · ") || "not set yet"} />
      <Fact label="What you do" value={entity.entity_description || "nothing recorded yet"} />
      <Fact label="Where you are" value={entity.entity_location || "not set yet"} />
      <Fact
        label="Your links"
        value={Object.entries(entity.entity_links ?? {})
          .filter(([, v]) => !!v)
          .map(([k, v]) => `${k}: ${v}`)
          .join(" · ") || "none linked yet"}
      />
    </dl>
  ) : null;

  return (
    <div className="mx-auto mt-8 max-w-2xl space-y-4 px-4">
      <div>
        <h1 className="text-2xl font-semibold">My profile</h1>
        <p className="text-sm text-muted-foreground">
          What Raisy knows about you, so it never asks you to type it twice.
        </p>
      </div>

      <Card>
        <CardContent className="space-y-4 pt-6">
          {load.state === "loading" && (
            <p className="text-sm text-muted-foreground">Reading your profile…</p>
          )}
          {load.state === "unavailable" && (
            <p className="text-sm text-muted-foreground">
              Not readable right now — {load.reason}. Nothing has been lost.
            </p>
          )}
          {load.state === "ready" && (
            <>
              {facts}
              <p className="border-t pt-3 text-xs text-muted-foreground">
                {entity!.extraction_url
                  ? `Brought in from ${entity!.extraction_url}`
                  : "No page is linked yet — link one below and Raisy can read it for you."}
              </p>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-3 pt-6">
          <div>
            <Label htmlFor="profile-address">The page that describes you</Label>
            <p className="mt-1 text-xs text-muted-foreground">
              Your own website or your profile page. Raisy reads it, keeps what it does not already
              know, and asks you about anything that disagrees with what it holds.
            </p>
          </div>
          <Input
            id="profile-address"
            inputMode="url"
            placeholder="https://…"
            value={address}
            disabled={busy || load.state !== "ready"}
            onChange={(e) => setDraft(e.target.value)}
          />
          {saveFailed && <p className="text-xs text-muted-foreground">{saveFailed}</p>}
          {broughtIn && !reviewOpen && (
            <p className="text-xs text-muted-foreground">
              Done — what the page said is now part of your profile above.
            </p>
          )}
          <div className="flex gap-2">
            <Button type="button" onClick={saveAddressThenRead} disabled={busy || load.state !== "ready"}>
              {busy ? "Bringing it in…" : "Bring in what that page says"}
            </Button>
            <Button type="button" variant="outline" asChild>
              <Link to="/settings">Your other settings</Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      {entity && (
        <ConflictReviewModal
          open={reviewOpen}
          entityId={entity.entity_id}
          onClose={() => setReviewOpen(false)}
          onResolved={() => { void onResolved(); }}
        />
      )}
    </div>
  );
};

const Fact = ({ label, value }: { label: string; value: string }) => (
  <div className="flex gap-3">
    <dt className="w-32 shrink-0 text-muted-foreground">{label}</dt>
    <dd className="min-w-0 break-words">{value}</dd>
  </div>
);

ProfilePage.path = "/profile";
